import React, { useState, useRef } from 'react';
import {X, Calculator, RotateCcw, Download, Circle, Waves} from 'lucide-react';
import html2canvas from 'html2canvas';

interface FlujoNormalCircularCalculatorProps {
  onClose: () => void;
}

interface Inputs {
  Gasto: string;
  Diametro: string;
  Pendiente: string;
  Rugosidad: string;
}

interface Results {
  Yn: string;
  Ahid: string;
  Pmojado: string;
  RHid: string;
  EAgua: string;
  Velocidad: string;
  NFroud: string;
  EnergiaE: string;
  Tflujo: string;
  doubleY?: { y1: number; y2: number };
}

const emptyInputs: Inputs = {
  Gasto: '',
  Diametro: '',
  Pendiente: '',
  Rugosidad: ''
};

const emptyResults: Results = {
  Yn: '',
  Ahid: '',
  Pmojado: '',
  RHid: '',
  EAgua: '',
  Velocidad: '',
  NFroud: '',
  EnergiaE: '',
  Tflujo: ''
};

const FlujoNormalCircularCalculator: React.FC<FlujoNormalCircularCalculatorProps> = ({ onClose }) => {
  const [inputs, setInputs] = useState<Inputs>(emptyInputs);
  const [results, setResults] = useState<Results>(emptyResults);
  const [error, setError] = useState<string>('');
  const [info, setInfo] = useState<string>('');
  const exportRef = useRef<HTMLDivElement>(null);

  const handleChange = (field: keyof Inputs) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputs(prev => ({ ...prev, [field]: e.target.value }));
  };

  const truncate4 = (val: number) => Math.trunc(val * 10000.0) / 10000.0;

  const calcular = () => {
    setError('');
    setInfo('');

    const q = Number(inputs.Gasto);
    const d = Number(inputs.Diametro);
    const n = Number(inputs.Rugosidad);
    const s = Number(inputs.Pendiente);

    if (q === 0 || d === 0 || n === 0 || s === 0 || isNaN(q) || isNaN(d) || isNaN(n) || isNaN(s)) {
      setError('Faltan datos o son irreales');
      setResults(emptyResults);
      return;
    }

    // Capacidad máxima a yn = 0.938*d
    let yn = 0.938 * d;
    let theta = Math.acos(1 - 2 * yn / d);
    let an = (1 / 4) * (theta - (1 / 2) * Math.sin(2 * theta)) * Math.pow(d, 2);
    let pn = theta * d;
    let rh = an / pn;
    let asl = d * Math.sin(theta);

    const qMax = (1 / n) * Math.pow(rh, 2 / 3) * Math.pow(s, 0.5) * an;
    if (q > qMax) {
      setError('Atención: Aumente el diámetro o disminuya el gasto');
      setResults(emptyResults);
      return;
    }

    // Bisección general 0..d
    let aa = 0;
    let bb = d;
    let res = 1;
    let iter = 0;
    while (Math.abs(res) > 0.0000000001 && iter < 1000) {
      yn = (bb + aa) / 2;
      theta = Math.acos(1 - 2 * yn / d);
      an = (1 / 4) * (theta - (1 / 2) * Math.sin(2 * theta)) * Math.pow(d, 2);
      pn = theta * d;
      rh = an / pn;
      asl = d * Math.sin(theta);
      res = ((1 / n) * Math.pow(rh, 2 / 3) * Math.pow(s, 0.5) * an) - q;
      if (res > 0) bb = yn;
      else aa = yn;
      iter++;
    }

    let v: number, fr: number, e: number, finalY = yn;
    let doubleY: { y1: number; y2: number } | undefined;

    if (yn / d > 0.81) {
      // Buscar dos raíces: y1 (rama baja 0..0.938d) y y2 (rama alta d..0.938d)
      let y1 = 0, y2 = 0;

      aa = 0;
      bb = d * 0.938;
      res = 1;
      iter = 0;
      while (Math.abs(res) > 0.0000000001 && iter < 1000) {
        y1 = (bb + aa) / 2;
        theta = Math.acos(1 - 2 * y1 / d);
        an = (1 / 4) * (theta - (1 / 2) * Math.sin(2 * theta)) * Math.pow(d, 2);
        pn = theta * d;
        rh = an / pn;
        asl = d * Math.sin(theta);
        res = ((1 / n) * Math.pow(rh, 2 / 3) * Math.pow(s, 0.5) * an) - q;
        if (res > 0) bb = y1;
        else aa = y1;
        iter++;
      }

      aa = d;
      bb = d * 0.938;
      res = 1;
      iter = 0;
      while (Math.abs(res) > 0.000000001 && iter < 1000) {
        y2 = (bb + aa) / 2;
        theta = Math.acos(1 - 2 * y2 / d);
        an = (1 / 4) * (theta - (1 / 2) * Math.sin(2 * theta)) * Math.pow(d, 2);
        pn = theta * d;
        rh = an / pn;
        asl = d * Math.sin(theta);
        res = ((1 / n) * Math.pow(rh, 2 / 3) * Math.pow(s, 0.5) * an) - q;
        if (res > 0) bb = y2;
        else aa = y2;
        iter++;
      }

      doubleY = { y1, y2 };
      setInfo(`Se encontraron dos tirantes normales: Y1 = ${truncate4(y1)} m y Y2 = ${truncate4(y2)} m. Los cálculos se realizaron con Y1.`);

      // Recalcular con y1
      finalY = y1;
      theta = Math.acos(1 - 2 * y1 / d);
      an = (1 / 4) * (theta - (1 / 2) * Math.sin(2 * theta)) * Math.pow(d, 2);
      pn = theta * d;
      rh = an / pn;
      asl = d * Math.sin(theta);
      v = q / an;
      fr = v / Math.pow((9.81 * an / asl), 0.5);
      e = y1 + Math.pow(v, 2) / (2 * 9.81);
    } else {
      v = q / an;
      fr = v / Math.pow((9.81 * an / asl), 0.5);
      e = yn + Math.pow(v, 2) / (2 * 9.81);
    }

    setResults({
      Yn: truncate4(finalY).toString(),
      Ahid: truncate4(an).toString(),
      Pmojado: truncate4(pn).toString(),
      RHid: truncate4(rh).toString(),
      EAgua: truncate4(asl).toString(),
      Velocidad: truncate4(v).toString(),
      NFroud: truncate4(fr).toString(),
      EnergiaE: truncate4(e).toString(),
      Tflujo: fr > 1 ? 'Supercrítico' : 'Subcrítico',
      doubleY
    });
  };

  const limpiar = () => {
    setInputs(emptyInputs);
    setResults(emptyResults);
    setError('');
    setInfo('');
  };

  const exportarPNG = async () => {
    if (!exportRef.current) return;
    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `flujo-normal-circular-openHH-${new Date().getTime()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error al exportar PNG:', err);
    }
  };

  const inputFields: { name: keyof Inputs; label: string; symbol: string; unit: string }[] = [
    { name: 'Gasto', label: 'Gasto o Caudal', symbol: 'Q', unit: 'm³/s' },
    { name: 'Diametro', label: 'Diámetro', symbol: 'D', unit: 'm' },
    { name: 'Pendiente', label: 'Pendiente', symbol: 'So', unit: 'm/m' },
    { name: 'Rugosidad', label: 'Rugosidad (Manning)', symbol: 'n', unit: 'Adim.' }
  ];

  const resultFields: { key: keyof Results; label: string; unit: string; highlight?: boolean }[] = [
    { key: 'Yn', label: 'Tirante Normal (Yn)', unit: 'm', highlight: true },
    { key: 'Ahid', label: 'Área Hidráulica', unit: 'm²' },
    { key: 'Pmojado', label: 'Perímetro Mojado', unit: 'm' },
    { key: 'RHid', label: 'Radio Hidráulico', unit: 'm' },
    { key: 'EAgua', label: 'Espejo del Agua', unit: 'm' },
    { key: 'Velocidad', label: 'Velocidad', unit: 'm/s' },
    { key: 'NFroud', label: 'Número de Froude', unit: 'Adim.' },
    { key: 'EnergiaE', label: 'Energía Específica', unit: 'm' },
    { key: 'Tflujo', label: 'Tipo de Flujo', unit: '' }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 relative">
          {/* Close button - sticky */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-gray-100 flex items-center justify-center shadow-md border border-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          <div ref={exportRef} className="bg-white rounded-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 p-6 rounded-t-2xl text-white">
              <div className="flex items-center gap-3 mb-2 pr-12">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Waves className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-emerald-100 text-sm font-medium uppercase tracking-wide">Flujo Normal</p>
                  <h2 className="text-2xl font-bold">Canal Circular</h2>
                </div>
              </div>
              <p className="text-emerald-50 text-sm leading-relaxed">
                Ingresa los datos y calcula el tirante normal, velocidad, número de Froude y demás parámetros hidráulicos en canales de sección circular.
              </p>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Datos a ingresar */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b-2 border-amber-400">
                  <Circle className="w-5 h-5 text-amber-600" />
                  <h3 className="font-bold text-amber-600 uppercase tracking-wide text-sm">Datos a ingresar</h3>
                </div>

                {/* Diagrama dinamico de la seccion circular */}
                <CircularChannelDiagram
                  tirante={Number(results.Yn) || 0}
                  diametro={Number(inputs.Diametro) || 0}
                />

                {inputFields.map(field => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="font-bold text-emerald-700">{field.symbol}</span> = {field.label}
                      <span className="text-gray-500 ml-1">({field.unit})</span>
                    </label>
                    <input
                      type="number"
                      value={inputs[field.name]}
                      onChange={handleChange(field.name)}
                      step="any"
                      min="0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                      placeholder="0.00"
                    />
                  </div>
                ))}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    ⚠️ {error}
                  </div>
                )}
                {info && (
                  <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
                    ℹ️ {info}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={calcular}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all"
                  >
                    <Calculator className="w-4 h-4" />
                    Calcular
                  </button>
                  <button
                    onClick={limpiar}
                    className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-5 rounded-lg transition-all"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Limpiar
                  </button>
                </div>
              </div>

              {/* Resultados */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b-2 border-orange-400">
                  <Waves className="w-5 h-5 text-orange-600" />
                  <h3 className="font-bold text-orange-600 uppercase tracking-wide text-sm">Resultados</h3>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 space-y-2.5 border border-emerald-100">
                  {resultFields.map(field => (
                    <div
                      key={field.key}
                      className={`flex items-center justify-between gap-3 py-2 px-3 rounded-lg ${
                        field.highlight ? 'bg-white shadow-sm border border-emerald-200' : ''
                      }`}
                    >
                      <span className={`text-sm ${field.highlight ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
                        {field.label}
                        {field.unit && <span className="text-xs text-gray-400 ml-1">({field.unit})</span>}
                      </span>
                      <span
                        className={`font-mono text-sm px-3 py-1 rounded ${
                          field.highlight
                            ? 'bg-emerald-600 text-white font-bold'
                            : results[field.key]
                            ? 'bg-white text-gray-800 border border-gray-200'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {(results[field.key] as string) || '—'}
                      </span>
                    </div>
                  ))}
                </div>

                {results.Tflujo && (
                  <div
                    className={`rounded-lg p-3 text-center font-semibold ${
                      results.Tflujo === 'Supercrítico'
                        ? 'bg-rose-100 text-rose-700 border border-rose-200'
                        : 'bg-blue-100 text-blue-700 border border-blue-200'
                    }`}
                  >
                    Régimen: {results.Tflujo}
                  </div>
                )}
              </div>
            </div>

            {/* Footer brand */}
            <div className="px-6 pb-4 pt-2 text-center border-t border-gray-100 mt-2">
              <p className="text-xs text-gray-400 font-medium">
                Generado por <span className="text-emerald-600 font-bold">openHH</span> · HHTools · {new Date().toLocaleDateString('es-ES')}
              </p>
            </div>
          </div>

          {/* Export button (outside captured area) */}
          <div className="px-6 pb-6">
            <button
              onClick={exportarPNG}
              disabled={!results.Yn}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-900 hover:to-slate-800 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all"
            >
              <Download className="w-4 h-4" />
              Exportar resultados como PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Diagrama SVG dinamico de la seccion transversal circular del canal.
 * Refleja en tiempo real D (diametro) e Yn (tirante normal calculado).
 * Muestra el agua llenando la tuberia, cotas con flechas y el porcentaje de llenado.
 */
const CircularChannelDiagram: React.FC<{ tirante: number; diametro: number }> = ({
  tirante,
  diametro,
}) => {
  const W = 360;
  const H = 220;
  const cx = W / 2;
  const cy = H / 2 - 5;

  // Radio de dibujo (mantiene el circulo dentro del SVG)
  const R = 70;

  const d = diametro > 0 ? diametro : 1;
  // Limitar tirante al diametro para no romper el dibujo
  const yClamped = Math.max(0, Math.min(tirante, d));
  const fillRatio = yClamped / d; // 0..1

  // Altura del agua dentro del circulo (en pixeles)
  const waterHeightPx = fillRatio * 2 * R;
  // Y de la superficie del agua (desde top del circulo)
  const waterTopY = cy + R - waterHeightPx;

  // Half-width del espejo del agua (cuerda en la altura waterTopY)
  const distFromCenter = cy - waterTopY;
  const halfChord =
    Math.abs(distFromCenter) <= R
      ? Math.sqrt(Math.max(0, R * R - distFromCenter * distFromCenter))
      : 0;

  const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');
  const pctLlenado = fillRatio * 100;

  // Estado del llenado
  const estado =
    diametro <= 0
      ? 'Sin diametro'
      : tirante <= 0
      ? 'Vacio'
      : tirante >= diametro
      ? 'Lleno'
      : tirante / diametro > 0.5
      ? 'Mayor a la mitad'
      : 'Menor a la mitad';

  // Path para el area de agua: se acumula desde el fondo hacia arriba, recortado al circulo.
  const waterPath =
    fillRatio <= 0
      ? ''
      : fillRatio >= 1
      ? `M ${cx - R},${cy} A ${R},${R} 0 1 1 ${cx + R},${cy} A ${R},${R} 0 1 1 ${cx - R},${cy} Z`
      : (() => {
          const xL = cx - halfChord;
          const xR = cx + halfChord;
          const yTop = waterTopY;
          const largeArc = fillRatio > 0.5 ? 1 : 0;
          return `M ${xL},${yTop} L ${xR},${yTop} A ${R},${R} 0 ${largeArc} 1 ${xL},${yTop} Z`;
        })();

  return (
    <div className="mb-2 bg-white rounded-lg border border-amber-300 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-3 py-2 bg-amber-100/60 border-b border-amber-200">
        <span className="text-xs font-semibold text-amber-900">Seccion transversal circular</span>
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white text-amber-700 border border-amber-300">
          {estado} · {fmt(pctLlenado)}%
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="circWaterGradFNC" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0891b2" stopOpacity="0.95" />
          </linearGradient>
          <radialGradient id="pipeGradFNC" cx="50%" cy="50%" r="50%">
            <stop offset="85%" stopColor="#f3f4f6" />
            <stop offset="100%" stopColor="#9ca3af" />
          </radialGradient>
          <pattern id="pipeWallFNC" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#9ca3af" strokeWidth="1.2" opacity="0.5" />
          </pattern>
          {/* ClipPath al circulo interior para que el agua nunca salga del tubo */}
          <clipPath id="circClipFNC">
            <circle cx={cx} cy={cy} r={R - 1} />
          </clipPath>
        </defs>

        {/* Pared exterior de la tuberia */}
        <circle cx={cx} cy={cy} r={R + 8} fill="url(#pipeWallFNC)" stroke="#6b7280" strokeWidth="1" />
        {/* Interior de la tuberia */}
        <circle cx={cx} cy={cy} r={R} fill="#fefce8" stroke="#374151" strokeWidth="2" />

        {/* Agua - recortada al circulo interior */}
        {waterPath && (
          <g clipPath="url(#circClipFNC)">
            <path d={waterPath} fill="url(#circWaterGradFNC)" stroke="none" />
            {fillRatio < 1 && halfChord > 6 && (
              <path
                d={`M ${cx - halfChord},${waterTopY} Q ${cx - halfChord / 2},${waterTopY - 3} ${cx},${waterTopY} T ${cx + halfChord},${waterTopY}`}
                fill="none"
                stroke="#ffffff"
                strokeWidth="1"
                opacity="0.7"
              />
            )}
          </g>
        )}
        {/* Borde interior del tubo encima del agua */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#374151" strokeWidth="2" />

        {/* Cota del diametro D */}
        <g>
          <line x1={cx - R} y1={cy - R - 18} x2={cx + R} y2={cy - R - 18} stroke="#1d4ed8" strokeWidth="1.2" />
          <polygon points={`${cx - R},${cy - R - 18} ${cx - R + 6},${cy - R - 22} ${cx - R + 6},${cy - R - 14}`} fill="#1d4ed8" />
          <polygon points={`${cx + R},${cy - R - 18} ${cx + R - 6},${cy - R - 22} ${cx + R - 6},${cy - R - 14}`} fill="#1d4ed8" />
          <line x1={cx - R} y1={cy - R} x2={cx - R} y2={cy - R - 22} stroke="#1d4ed8" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
          <line x1={cx + R} y1={cy - R} x2={cx + R} y2={cy - R - 22} stroke="#1d4ed8" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
          <text x={cx} y={cy - R - 24} textAnchor="middle" fill="#1e40af" fontSize="11" fontWeight="700">
            D = {fmt(diametro)} m
          </text>
        </g>

        {/* Cota del tirante Yn */}
        <g>
          <line x1={cx + R + 22} y1={waterTopY} x2={cx + R + 22} y2={cy + R} stroke="#dc2626" strokeWidth="1.2" />
          {tirante > 0 && (
            <>
              <polygon points={`${cx + R + 22},${waterTopY} ${cx + R + 18},${waterTopY + 6} ${cx + R + 26},${waterTopY + 6}`} fill="#dc2626" />
              <polygon points={`${cx + R + 22},${cy + R} ${cx + R + 18},${cy + R - 6} ${cx + R + 26},${cy + R - 6}`} fill="#dc2626" />
            </>
          )}
          <line x1={cx + halfChord} y1={waterTopY} x2={cx + R + 26} y2={waterTopY} stroke="#dc2626" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
          <line x1={cx} y1={cy + R} x2={cx + R + 26} y2={cy + R} stroke="#dc2626" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
          <text x={cx + R + 30} y={(waterTopY + cy + R) / 2 + 4} fill="#b91c1c" fontSize="11" fontWeight="700">
            Yn = {fmt(tirante)} m
          </text>
        </g>

        {/* Linea de fondo (referencia) */}
        <line x1={cx - R - 4} y1={cy + R} x2={cx + R + 4} y2={cy + R} stroke="#374151" strokeWidth="1" />

        {/* Centro del circulo */}
        <line x1={cx - 3} y1={cy} x2={cx + 3} y2={cy} stroke="#6b7280" strokeWidth="0.8" />
        <line x1={cx} y1={cy - 3} x2={cx} y2={cy + 3} stroke="#6b7280" strokeWidth="0.8" />
      </svg>
    </div>
  );
};

export default FlujoNormalCircularCalculator;
